import { renameConnectorFunctionReferences } from "./renameConnectorFunctionReferences";

export type DynamicReferenceScope = "data" | "control";

type ModReferenceFields = {
  dataKey?: string;
  liveControlKey?: string;
  expression?: string;
  functionString?: string;
  value?: unknown;
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const replaceExpressionReferences = (
  expression: string,
  oldKey: string,
  newKey: string,
  roots: string[]
) => {
  const escapedOldKey = escapeRegExp(oldKey);

  return roots.reduce((acc, root) => {
    const rootPattern = escapeRegExp(root);
    return acc.replace(
      new RegExp(
        `${rootPattern}\\.${escapedOldKey}(?=\\.|[^a-zA-Z0-9_]|$)`,
        "g"
      ),
      `${root}.${newKey}`
    );
  }, expression);
};

const replaceTemplateReferences = (
  value: string,
  oldKey: string,
  newKey: string
) => {
  const escapedOldKey = escapeRegExp(oldKey);

  return value.replace(
    new RegExp(`\\$\\{${escapedOldKey}(?=\\.|[^a-zA-Z0-9_}]|\\}|$)`, "g"),
    "${" + newKey
  );
};

export const getRenamedModDataFields = (
  modData: ModReferenceFields,
  oldKey: string,
  newKey: string,
  scope: DynamicReferenceScope
) => {
  const updates: Partial<ModReferenceFields> = {};
  const roots = scope === "data" ? ["data"] : ["control", "liveControl"];

  if (scope === "data" && modData.dataKey) {
    if (modData.dataKey === oldKey) {
      updates.dataKey = newKey;
    } else if (modData.dataKey.startsWith(oldKey + ".")) {
      updates.dataKey = newKey + modData.dataKey.slice(oldKey.length);
    }
  }

  if (scope === "control" && modData.liveControlKey) {
    if (modData.liveControlKey === oldKey) {
      updates.liveControlKey = newKey;
    } else if (modData.liveControlKey.startsWith(oldKey + ".")) {
      updates.liveControlKey =
        newKey + modData.liveControlKey.slice(oldKey.length);
    }
  }

  for (const field of ["expression", "functionString"] as const) {
    if (typeof modData[field] !== "string") continue;
    const updatedExpression =
      field === "functionString"
        ? renameConnectorFunctionReferences(
            modData[field]!,
            oldKey,
            newKey,
            scope === "control" ? [...roots, "liveData"] : roots
          )
        : replaceExpressionReferences(modData[field]!, oldKey, newKey, roots);
    if (updatedExpression !== modData[field]) {
      updates[field] = updatedExpression;
    }
  }

  if (scope === "data" && typeof modData.value === "string") {
    const updatedValue = replaceTemplateReferences(
      modData.value,
      oldKey,
      newKey
    );
    if (updatedValue !== modData.value) {
      updates.value = updatedValue;
    }
  }
  return updates;
};
